import app from '../../forum/app';
import Post from './Post';
import classList from '../../common/utils/classList';
import PostUser from './PostUser';
import PostMeta from './PostMeta';
import PostEdited from './PostEdited';
import EditPostComposer from './EditPostComposer';
import ItemList from '../../common/utils/ItemList';
import listItems from '../../common/helpers/listItems';
import Button from '../../common/components/Button';
import ComposerPostPreview from './ComposerPostPreview';
import ResponseItemsBtn from '../../common/components/ResponseItemsBtn';
import PicturePreviewModal from '../dianLiang/PicturePreviewModal';
import AlertUtil from '../dianLiang/alertUtil';

/**
 * The `CommentPost` component displays a standard `comment`-typed post. This
 * includes a number of item lists (controls, header, and footer) surrounding
 * the post's HTML content.
 *
 * ### Attrs
 *
 * - `post`
 */
export default class CommentPost extends Post {
  oninit(vnode) {
    super.oninit(vnode);
    /**
     * If the post has been hidden, then this flag determines whether or not its
     * content has been expanded.
     *
     * @type {Boolean}
     */
    this.revealContent = false;

    /**
     * Whether or not the user hover card inside of PostUser is visible.
     * The property must be managed in CommentPost to be able to use it in the subtree check
     *
     * @type {Boolean}
     */
    this.cardVisible = false;

    this.subtree.check(
      () => this.cardVisible,
      () => this.isEditing(),
      () => this.revealContent
    );
    this.isExpanded = false; // 初始状态为未展开
    this.likeGetState = false;
    this.isActivity = false;
    this.activityStatus = '';
    this.localeType = '';
    this.savePost = '';
    if (this.attrs.post.number() == 1 && this.attrs.discussion.pkgActivity()) {
      this.isActivity = true;
      this.activityStatus = this.attrs.discussion.pkgActivity()?.status();
    }
  }
  // 计算内容的行数
  calculateLineCount(content) {
    const lineHeight = 20; // 假设行高为20像素
    const containerHeight = 80; // 四行的高度
    const contentElement = document.createElement('div');
    contentElement.innerHTML = content;
    contentElement.style.position = 'absolute';
    contentElement.style.visibility = 'hidden';
    document.body.appendChild(contentElement);
    const contentHeight = contentElement.offsetHeight;
    document.body.removeChild(contentElement);
    return contentHeight > containerHeight;
  }
  formatContent(html) {
    return html.replace(/\n{3,}/g, (match) => {
      const count = match.length;
      return '<br>'.repeat(count - 2);
    });
  }

  content() {
    const contentHtml = this.attrs.post.contentHtml();
    this.refreshContent();

    const isContentTooLong = this.calculateLineCount(contentHtml);
    let atUser = '';
    if (this.attrs.post.replyTo() != 0 && this.attrs.post.replyPost() != 0 && this.attrs.post.replyPost() != null) {
      if (this.attrs.post.replyToPost()) {
        atUser = '@' + this.attrs.post.replyToPost().user().nickname();
      }
    }
    const formatted = this.formatContent(this.contentHtml);

    return super.content().concat([
      // <ResponseItemsBtn />,
      <div className="Post-box">
        <header className="Post-header Post-header-lin">
          <ul>{listItems(this.headerItems().toArray())}</ul>
        </header>
        <span className="Post-body Post-body-lin">
          {this.attrs.post.number() == 1 && (
            <p>
              {this.isActivity && <span>[{this.activityStatus}]</span>}
              <span>{this.title}</span>
            </p>
          )}
          {
            // isContentTooLong?
            // <>
            // <div className={this.isExpanded?'':'ClampedContent'}>{m.trust(contentHtml)}</div>
            //       <button
            //       className="ExpandCollapseButton"
            //       onclick={() => (this.isExpanded = !this.isExpanded)}
            //     >
            //       {this.isExpanded ? '收起' : '展开'}
            //     </button>
            // </>:
            this.isEditing() ? (
              <ComposerPostPreview className="Post-preview" composer={app.composer} />
            ) : atUser ? (
              m.trust('<span class="at-user-reference">' + atUser + '</span><br/>' + formatted)
            ) : (
              m.trust(formatted)
            )
          }
          {this.attrs.post.number() == 1 && this.attrs.discussion.mainImg() && <img src={this.attrs.discussion.mainImg()} loading="lazy" />}
        </span>
      </div>,
    ]);
  }

  refreshContent() {
    const contentHtml = this.attrs.post.contentHtml();
    const title = this.attrs.post.number() == 1 ? this.attrs.discussion.title() : '';

    // If the post content has changed since the last render, we'll run through
    // all of the <script> tags in the content and evaluate them. This is
    // necessary because TextFormatter outputs them for e.g. syntax highlighting.
    if (this.contentHtml !== contentHtml) {
      this.$('.Post-body script').each(function () {
        const script = document.createElement('script');
        script.textContent = this.textContent;
        Array.from(this.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
        this.parentNode.replaceChild(script, this);
      });
    }
    if (this.isEditing()) return;
    if (contentHtml === this.contentHtml && title === this.title) return;
    if (this.localeType === '') {
      this.contentHtml = contentHtml;
      this.title = title;
    } else {
      this.translateText(this.attrs.post, this.localeType, contentHtml, title);
    }
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.setupImageClickListeners();
    this.refreshContent();
  }

  onupdate(vnode) {
    super.onupdate(vnode);
    this.setupImageClickListeners();
    this.refreshContent();
  }

  /**
   * 为当前组件内的 .Post-body-lin 图片绑定点击预览监听器。
   * 使用 data-img-preview 属性防止重复绑定，确保 contentKey 重建 DOM 后监听器也能恢复。
   */
  setupImageClickListeners() {
    const postBodyElements = this.element.querySelectorAll('.Post-body-lin');
    postBodyElements.forEach((element) => {
      element.querySelectorAll('img:not([data-img-preview])').forEach((img) => {
        img.setAttribute('data-img-preview', 'true');
        img.addEventListener('click', function (event) {
          event.stopPropagation();
          var imageUrl = event.target.src;
          app.modal.show(PicturePreviewModal, { imgList: imageUrl });
        });
      });
    });
  }

  isEditing() {
    return app.composer.bodyMatches(EditPostComposer, { post: this.attrs.post });
  }

  elementAttrs() {
    const post = this.attrs.post;
    const attrs = super.elementAttrs();

    attrs.className = classList(attrs.className, 'CommentPost', {
      'Post--renderFailed': post.renderFailed(),
      'Post--hidden': post.isHidden(),
      'Post--edited': post.isEdited(),
      revealContent: this.revealContent,
      editing: this.isEditing(),
    });

    if (this.isEditing()) attrs['aria-busy'] = 'true';

    return attrs;
  }

  /**
   * Toggle the visibility of a hidden post's content.
   */
  toggleContent() {
    this.revealContent = !this.revealContent;
  }

  /**
   * Build an item list for the post's header.
   *
   * @return {ItemList<import('mithril').Children>}
   */
  headerItems() {
    const items = new ItemList();
    this.post = this.attrs.post;
    this.discussion = this.attrs.discussion;

    items.add(
      'user',
      <PostUser
        post={this.post}
        discussion={this.discussion}
        cardVisible={this.cardVisible}
        oncardshow={() => {
          this.cardVisible = true;
          m.redraw();
        }}
        oncardhide={() => {
          this.cardVisible = false;
          m.redraw();
        }}
      />,
      100
    );
    items.add('meta', <PostMeta post={this.post} />, 90);

    // if (this.post.isEdited() && !this.post.isHidden()) {
    //   items.add('edited', <PostEdited post={this.post} />);
    // }

    // If the post is hidden, add a button that allows toggling the visibility
    // of the post's content.
    if (this.post.isHidden()) {
      items.add(
        'toggle',
        <Button className="Button Button--default Button--more" icon="fas fa-ellipsis-h" onclick={this.toggleContent.bind(this)} />
      );
    }

    return items;
  }

  normalizeJson(json) {
    if (!json) return '';
    // 将字符串映射转换为 JavaScript 对象
    const obj = JSON.parse(json);
    // 创建 Map 对象并初始化
    const map = new Map(Object.entries(obj));
    // 获取特定键的值
    return map.get(app.data.locale);
  }

  translateText(post, language, content, title) {
    // if (this.savePost[0] === this.contentHtml && this.savePost[1] === language && this.savePost[2] === content && this.savePost[3] === title) return;
    // let route = 0;
    // if (post.number() == 1 && post.discussion().titleLang()) {
    //   // 将字符串映射转换为 JavaScript 对象
    //   const obj = JSON.parse(post.discussion().titleLang());
    //   // 创建 Map 对象并初始化
    //   const map = new Map(Object.entries(obj));
    //   // 获取特定键的值
    //   let titleLang = map.get(language);
    //   if (titleLang) {
    //     route += 1;
    //     this.title = titleLang;
    //   }
    // }
    // this.savePost = [this.contentHtml, this.localeType, content, title];
    // if (post.contentLang()) {
    //   // 将字符串映射转换为 JavaScript 对象
    //   const obj = JSON.parse(post.contentLang());
    //   // 创建 Map 对象并初始化
    //   const map = new Map(Object.entries(obj));
    //   // 获取特定键的值
    //   let contentLang = map.get(language);
    //   if (contentLang) {
    //     route += 1;
    //     this.savePost = [contentLang, language, content, title];
    //     this.localeType = language;
    //     this.contentHtml = contentLang;
    //     // return contentLang;
    //   }
    // }
    // if (route != 2) {
    //   setTimeout(() => {
    //     app
    //       .request({
    //         method: 'GET',
    //         url: app.forum.attribute('apiUrl') + `/getTranslate`,
    //         params: {
    //           content: post.contentHtml(),
    //           language: language,
    //           postId: post.id(),
    //         },
    //       })
    //       .then((res) => {
    //         if (res) {
    //           let contentHtml = res['contentHtml'];
    //           let titleHtml = res['titleHtml'];
    //           if (post.number() == 1) {
    //             this.title = titleHtml;
    //           }
    //           this.savePost = [contentHtml, language, content, title];
    //           this.localeType = language;
    //           this.contentHtml = contentHtml;
    //           m.redraw();
    //         } else {
    //           AlertUtil.onError(app, app.translator.trans('dl-response.forum.warn.translate_error'));
    //           return;
    //         }
    //       });
    //   }, 500);
    // }
  }
}
